import { styled } from '@mui/material/styles';
import { Paper, Typography, Box, Tooltip, IconButton } from '@mui/material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { useCommonTranslation } from '../../../../hooks/useCommonTranslation';
import { Client } from '../../../../data/models/client.model';
import { TransportationBenefit } from '../../../../data/models/transportation-benefit.model';
import TransportationBenefitList from './TransportationBenefitList';
import { useIsSuperReaderUser } from '../../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'ClientTransportationBenefits';

const classes = {
  container: `${PREFIX}-container`,
  details: `${PREFIX}-details`,
  header: `${PREFIX}-header`,
  subheader: `${PREFIX}-subheader`,
  title: `${PREFIX}-title`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    minWidth: 400,
  },

  [`& .${classes.details}`]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },

  [`& .${classes.header}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },

  [`& .${classes.subheader}`]: {
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },

  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontSize: 21,
    fontWeight: 700,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
}));

interface ClientTransportationBenefitsProps {
  client?: Client;
  transportationBenefits: TransportationBenefit[];
  onAdd: () => void;
  onActivate: (evt: any, transportationBenefit: TransportationBenefit) => void;
  onDeactivate: (
    evt: any,
    transportationBenefit: TransportationBenefit,
  ) => void;
  onDelete: (evt: any, transportationBenefit: TransportationBenefit) => void;
  onEdit: (evt: any, transportationBenefit: TransportationBenefit) => void;
  onStartDrag: (transportationBenefit: TransportationBenefit) => void;
  onEndDrag: (transportationBenefit: TransportationBenefit) => void;
}

export default function ClientTransportationBenefits({
  client,
  transportationBenefits,
  onAdd,
  onActivate,
  onDeactivate,
  onEdit,
  onDelete,
  onStartDrag,
  onEndDrag,
}: ClientTransportationBenefitsProps) {
  const t = useCommonTranslation();

  const isSuperReaderUser = useIsSuperReaderUser();

  return (
    <StyledBox className={classes.container}>
      <Paper elevation={2} className={classes.details}>
        <Box className={classes.header}>
          <Typography className={classes.title}>
            {t('client.transportationBenefits')}
          </Typography>
          {!isSuperReaderUser && (
            <Tooltip title={t('client.addTransportationBenefit')}>
              <span>
                <IconButton
                  data-testid="add-transport-benefit"
                  onClick={onAdd}
                  color="primary"
                  size="large">
                  <AddCircleOutlineOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
        <Box className={classes.subheader}>
          {t('client.dragTransportationBenefit')}
        </Box>
        {client && (
          <TransportationBenefitList
            client={client}
            transportationBenefits={transportationBenefits}
            onActivate={onActivate}
            onDeactivate={onDeactivate}
            onEdit={onEdit}
            onDelete={onDelete}
            onStartDrag={onStartDrag}
            onEndDrag={onEndDrag}
          />
        )}
      </Paper>
    </StyledBox>
  );
}
